import type { Views } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from './supabase.ts';

/**
 * Central de notificações do médico (build-prompt §4.4).
 *
 * Lê a view `doctor_alerts`, que já resolve o paciente, a regra que disparou e a
 * entrega em cada canal. A view devolve UM item por evento (a linha do canal
 * `panel`), não um por canal: o médico precisa saber que o paciente piorou, não
 * que a mesma notícia foi tentada por dois meios. O estado de cada canal vem em
 * `delivery`, para ele conseguir ver que o e-mail falhou sem que isso vire um
 * segundo item disputando a atenção.
 */

export type DoctorAlert = Views<'doctor_alerts'>;

/** Estado de entrega por canal, como a view monta em `delivery`. */
export interface ChannelDelivery {
  status: 'pending' | 'sent' | 'failed' | 'acknowledged';
  error: string | null;
}

/**
 * O gerador tipa a coluna como `Json`, que inclui array — mas a view monta um
 * OBJETO com `jsonb_object_agg`, indexado por canal. A guarda de array não é
 * cerimônia de tipo: sem ela, um `delivery` inesperado viraria `.entries()`
 * sobre um array e renderizaria índices numéricos como se fossem canais.
 */
export function parseDelivery(raw: DoctorAlert['delivery']): Record<string, ChannelDelivery> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw as unknown as Record<string, ChannelDelivery>;
}

export function useAlerts() {
  return useQuery({
    queryKey: ['doctor-alerts'],
    queryFn: async (): Promise<DoctorAlert[]> => {
      // Sem WHERE de médico aqui: a view já resolve `current_doctor_id()` por
      // dentro, do mesmo jeito que `doctor_patient_triage`.
      const { data, error } = await supabase
        .from('doctor_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Quantos ainda não foram vistos — alimenta o indicador na barra do painel. */
export function useUnacknowledgedCount(): number {
  const { data } = useAlerts();
  return (data ?? []).filter((a) => a.acknowledged_at === null).length;
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase.rpc('acknowledge_alert', { p_alert_id: alertId });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['doctor-alerts'] }),
  });
}

/**
 * Push no painel (§5.2, canal `panel`).
 *
 * É o canal mais rápido dos três, e o único que não depende de serviço externo
 * nenhum: a entrega É a linha existir em `alerts`, e isto aqui só faz ela
 * aparecer sem o médico recarregar a página.
 *
 * Assina INSERT e UPDATE: o insert traz o alerta novo, e o update é o que
 * reflete o e-mail saindo de `pending` para `sent` na tela aberta.
 */
export function useAlertsRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel('doctor-alerts')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'alerts' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['doctor-alerts'] });
          // A triagem também muda: um alerta é a consequência visível de o
          // semáforo ter piorado, e a lista precisa refletir o estado novo.
          queryClient.invalidateQueries({ queryKey: ['doctor-triage'] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
